import React from 'react';
import { X, FileText, CheckCircle2 } from 'lucide-react';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const eur = (n) => `€${Number(n || 0).toLocaleString()}`;
const cap = (s) => String(s || '').replace(/^\w/, (c) => c.toUpperCase());

// Full invoice for one subscription payment — plan, period, the price breakdown
// (list price, upgrade credit, net paid) + payment method. Works for old rows too
// (no originalAmount/creditApplied → just shows the paid amount, no credit line).
const InvoiceModal = ({ payment, doctorId, onClose }) => {
  if (!payment) return null;
  const net = parseFloat(payment.amount) || 0;
  const orig = payment.originalAmount !== '' && payment.originalAmount != null
    ? (parseFloat(payment.originalAmount) || net)
    : net;
  const credit = parseFloat(payment.creditApplied) || 0;
  const hasCredit = credit > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] px-4" data-testid="invoice-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#4ABDBD]" />
            <h2 className="text-lg font-bold text-[#1a365d]">Invoice</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 text-sm">
          <div className="flex justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs text-slate-400">Reference</p>
              <p className="font-mono text-xs text-gray-700 break-all">{payment.reference || payment.id}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-slate-400">Date</p>
              <p className="text-gray-700">{fmtDate(payment.createdAt)}</p>
            </div>
          </div>

          <div className="flex justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs text-slate-400">Billed to</p>
              <p className="text-gray-700 break-all">{doctorId}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-slate-400">Status</p>
              <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold" data-testid="invoice-status">
                <CheckCircle2 className="w-4 h-4" /> {cap(payment.status || 'paid')}
              </span>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3">
            <p className="font-semibold text-[#1a365d]">{payment.packageName || 'Subscription'}</p>
            <p className="text-xs text-slate-400">{fmtDate(payment.periodStart)} → {fmtDate(payment.periodEnd)}</p>
          </div>

          <div className="border-t border-gray-100 pt-3 space-y-1.5">
            <div className="flex justify-between text-gray-600">
              <span>Plan price</span>
              <span>{eur(orig)}</span>
            </div>
            {hasCredit && (
              <div className="flex justify-between text-emerald-600" data-testid="invoice-credit">
                <span>Upgrade credit (unused days)</span>
                <span>−{eur(credit)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-100 font-bold text-[#1a365d]">
              <span>Total paid</span>
              <span data-testid="invoice-total">{eur(net)}</span>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3 flex justify-between text-gray-600">
            <span className="text-slate-400">Payment method</span>
            <span>Card ···· {payment.cardLast4 || '0000'}</span>
          </div>

          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            <span>Demo invoice — recorded by the simulated checkout (no real charge was made).</span>
          </div>
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200 flex gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors"
          >
            Print
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold text-sm hover:shadow-lg transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
